import { useState, useEffect, useRef } from "react";
import { Button, Form, Modal, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

const BASE = "https://dailydish.in";
const PRODUCTS_API = `${BASE}/api/production/products`;
const FOOD_SEARCH_API = `${BASE}/api/production/products/search-food-items`;
const COOKABLE_API = `${BASE}/api/production/cookable-items`;

const TYPE_UNIT = { weight: "kg", pieces: "pcs", pack: "pack" };

function FoodSearch({ value, onSelect, disabled }) {
  const [query, setQuery] = useState(value?.foodname || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value?.foodname || ""); }, [value]);

  useEffect(() => {
    if (!query.trim() || (value && query === value.foodname)) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(FOOD_SEARCH_API, { params: { q: query } });
        if (res.data.success) { setResults(res.data.data); setOpen(true); }
      } catch { } finally { setLoading(false); }
    }, 300);
  }, [query]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Form.Control
          placeholder="Type food name to search..."
          value={query}
          onChange={e => { setQuery(e.target.value); if (value) onSelect(null); }}
          disabled={disabled}
          style={{ borderRadius: 8 }}
        />
        {loading && <Spinner size="sm" />}
      </div>
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 1050,
          background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 260, overflowY: "auto",
        }}>
          {results.map(food => (
            <div key={food._id}
              onMouseDown={() => { onSelect(food); setQuery(food.foodname); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
            >
              {food.Foodgallery?.[0]?.image2
                ? <img src={food.Foodgallery[0].image2} alt="" style={{ width: 38, height: 38, objectFit: "cover", borderRadius: 8 }} />
                : <div style={{ width: 38, height: 38, background: "#f0f0f0", borderRadius: 8 }} />
              }
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{food.foodname}</div>
                {food.foodmealtype && <div style={{ fontSize: 12, color: "#aaa" }}>{food.foodmealtype}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComponentsBuilder({ components, cookableItems, onChange }) {
  const addRow = () => onChange([...components, { cookableItemId: "", qty: "" }]);
  const removeRow = (i) => onChange(components.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => onChange(components.map((c, idx) => idx === i ? { ...c, [field]: val } : c));

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 10, overflow: "hidden" }}>
      {components.map((comp, i) => {
        const ci = cookableItems.find(c => c._id === comp.cookableItemId);
        const unit = ci ? TYPE_UNIT[ci.outputType] : "";
        const ph = ci?.outputType === "weight" ? "0.45" : "1";
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
            <Form.Select value={comp.cookableItemId} onChange={e => updateRow(i, "cookableItemId", e.target.value)} style={{ flex: 1, borderRadius: 8, fontSize: 14 }}>
              <option value="">Select cookable item</option>
              {cookableItems.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </Form.Select>
            <Form.Control
              type="number" step="0.01" min="0" placeholder={ph}
              value={comp.qty} onChange={e => updateRow(i, "qty", e.target.value)}
              style={{ width: 90, borderRadius: 8, fontSize: 14 }}
            />
            <span style={{ width: 36, fontSize: 12, color: "#aaa", textAlign: "center" }}>{unit}</span>
            <button onClick={() => removeRow(i)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          </div>
        );
      })}
      <button onClick={addRow} style={{ width: "100%", padding: "10px", background: "#f8f8f8", border: "none", color: "#1D9E75", fontWeight: 500, fontSize: 14, cursor: "pointer" }}>
        + Add cookable item
      </button>
    </div>
  );
}

function ProductModal({ show, onHide, initial, cookableItems, onSave }) {
  const isEdit = !!initial;
  const [selectedFood, setSelectedFood] = useState(null);
  const [components, setComponents] = useState([{ cookableItemId: "", qty: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (isEdit) {
      setSelectedFood({ _id: initial.foodItemId, foodname: initial.name, Foodgallery: initial.foodItemImage ? [{ image2: initial.foodItemImage }] : [] });
      setComponents(initial.components.map(c => ({ cookableItemId: c.cookableItemId?._id || c.cookableItemId, qty: c.qty })));
    } else {
      setSelectedFood(null);
      setComponents([{ cookableItemId: "", qty: "" }]);
    }
  }, [show]);

  const handleSave = async () => {
    if (!selectedFood) return toast.error("Select a food item");
    if (!components.length || components.some(c => !c.cookableItemId || !c.qty)) return toast.error("Fill all component fields");
    setSaving(true);
    try {
      const payload = {
        foodItemId: selectedFood._id,
        foodItemImage: selectedFood.Foodgallery?.[0]?.image2 || initial?.foodItemImage || "",
        name: selectedFood.foodname,
        components: components.map(c => ({ cookableItemId: c.cookableItemId, qty: parseFloat(c.qty) })),
      };
      const res = isEdit
        ? await axios.put(`${PRODUCTS_API}/${initial._id}`, payload)
        : await axios.post(PRODUCTS_API, payload);
      if (res.data.success) { onSave(res.data.data, isEdit); onHide(); }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>{isEdit ? "Edit Production Product" : "Add Production Product"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500, fontSize: 13, color: "#555" }}>Food item</Form.Label>
          <FoodSearch value={selectedFood} onSelect={setSelectedFood} disabled={isEdit} />
          {isEdit && <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>Food item cannot be changed when editing. Delete and re-add if needed.</div>}
        </Form.Group>

        {selectedFood?.Foodgallery?.[0]?.image2 && (
          <div className="d-flex align-items-center gap-2 mb-3 p-2" style={{ background: "#f8f9fa", borderRadius: 8 }}>
            <img src={selectedFood.Foodgallery[0].image2} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedFood.foodname}</div>
              <div style={{ fontSize: 12, color: "#1D9E75" }}>✓ Image auto-filled from product catalog</div>
            </div>
          </div>
        )}

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 500, fontSize: 13, color: "#555" }}>Cookable item components</Form.Label>
          <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8 }}>Weight in kg — 0.45 = 450g, 0.15 = 150g</div>
          <ComponentsBuilder components={components} cookableItems={cookableItems} onChange={setComponents} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #f0f0f0" }}>
        <Button variant="light" onClick={onHide} style={{ borderRadius: 8 }}>Cancel</Button>
        <Button variant="success" onClick={handleSave} disabled={saving} style={{ borderRadius: 8 }}>
          {saving ? <Spinner size="sm" /> : isEdit ? "Save Changes" : "Add Product"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function ProductionProductsTab() {
  const [products, setProducts] = useState([]);
  const [cookableItems, setCookableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([axios.get(PRODUCTS_API), axios.get(COOKABLE_API)]);
      if (pRes.data.success) setProducts(pRes.data.data);
      if (cRes.data.success) setCookableItems(cRes.data.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = (saved, isEdit) => {
    if (isEdit) setProducts(prev => prev.map(p => p._id === saved._id ? saved : p));
    else setProducts(prev => [saved, ...prev]);
    toast.success(isEdit ? "Product updated" : "Product added");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this production product?")) return;
    try {
      await axios.delete(`${PRODUCTS_API}/${id}`);
      setProducts(prev => prev.filter(p => p._id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Production Products</h5>
          <div style={{ fontSize: 13, color: "#888" }}>Map food items to their cookable ingredients (recipe)</div>
        </div>
        <Button variant="success" onClick={() => { setEditing(null); setShow(true); }} style={{ borderRadius: 8, fontWeight: 500 }}>+ Add Product</Button>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #eee", overflow: "hidden" }}>
        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : products.length === 0 ? (
          <div className="text-center py-5" style={{ color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🍱</div>
            <div style={{ fontWeight: 500 }}>No production products yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Map each food item to its cookable ingredients</div>
          </div>
        ) : products.map((p, i) => (
          <div key={p._id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
            padding: "14px 20px", borderBottom: i < products.length - 1 ? "1px solid #f0f0f0" : "none",
            background: "#fff", transition: "background 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
          >
            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
              {p.foodItemImage
                ? <img src={p.foodItemImage} alt={p.name} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 10, border: "1px solid #eee", flexShrink: 0 }} />
                : <div style={{ width: 52, height: 52, background: "#f0f0f0", borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🍽️</div>
              }
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{p.name}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {p.components.map((c, ci) => {
                    const item = c.cookableItemId;
                    if (!item) return null;
                    return (
                      <span key={ci} style={{ fontSize: 12, padding: "2px 10px", borderRadius: 20, background: "#f0f0f0", color: "#555", fontWeight: 500 }}>
                        {item.name} — {c.qty}{TYPE_UNIT[item.outputType]}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
              <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8 }} onClick={() => { setEditing(p); setShow(true); }}>Edit</Button>
              <Button size="sm" variant="outline-danger" style={{ borderRadius: 8 }} onClick={() => handleDelete(p._id)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <ProductModal show={show} onHide={() => setShow(false)} initial={editing} cookableItems={cookableItems} onSave={handleSave} />
    </>
  );
}
